<template>
  <el-dialog
    :title="dialogTitle"
    :visible.sync="dialogVisible"
    width="960px"
    top="5vh"
    append-to-body
    custom-class="dorm-bed-dialog"
    :close-on-click-modal="false"
    @close="handleClose"
  >
    <div v-if="!room" class="bed-loading">
      <i class="el-icon-loading" />
      <span>正在加载房间数据...</span>
    </div>
    <div v-if="room" class="bed-mgr">
      <div class="bed-layout" v-if="room.RoomType !== 2">
        <!-- 左侧床位 -->
        <aside class="bed-left">
          <div class="panel-head">
            <div class="panel-title">床位选择</div>
            <div class="panel-sub">
              <template v-if="room.RoomType === 1">宿管房 · 额定 {{ bedCount }} / 已住 {{ occupiedCount }}</template>
              <template v-else>学生房 · 额定 {{ room.Capacity || bedCount }} / 已住 {{ occupiedCount }}</template>
            </div>
          </div>
          <div class="beds-grid" :class="bedGridClass">
            <div
              v-for="bed in room.Beds"
              :key="bed.Id"
              class="bed-card"
              :class="{
                active: String(selectedBedId) === String(bed.Id),
                occupied: isOccupied(bed),
                matched: isBedSearchMatched(bed)
              }"
              @click="selectBed(bed)"
            >
              <div class="bed-card-head">
                <div class="bed-no">{{ bed.Number }}号床</div>
                <template v-if="isOccupied(bed)">
                  <span v-if="bed.UserId > 0" class="tag-boarder">学生</span>
                  <span v-else class="tag-teacher">教师</span>
                </template>
              </div>
              <div v-if="isOccupied(bed)" class="bed-person">
                <div class="bed-avatar">
                  <img v-if="bed.HeadPhotoImage" v-oss-src="bed.HeadPhotoImage" alt="" />
                  <span v-else>{{ occupantInitial(bed) }}</span>
                </div>
                <div class="bed-person-main">
                  <div class="name">{{ bed.OccupantName || '已入住' }}</div>
                  <div class="sub">{{ bed.ClassName || bed.OccupantNo || '' }}</div>
                </div>
                <span
                  v-if="bed.UserId > 0"
                  class="tag-presence"
                  :class="presenceClass(bed.PresenceStatus)"
                  :title="presenceTitle(bed)"
                >{{ presenceShort(bed.PresenceStatus) }}</span>
              </div>
              <div v-else class="bed-empty">
                <i class="el-icon-plus"></i>
                <span>空闲床位</span>
              </div>
            </div>
          </div>
        </aside>

        <!-- 右侧办理 -->
        <section class="bed-right">
          <div v-if="!selectedBed" class="empty-pane">
            <i class="el-icon-s-grid"></i>
            <p>请选择左侧床位</p>
            <span>选中后办理入住或退宿</span>
          </div>

          <template v-else>
            <div class="panel-head">
              <div class="panel-title">{{ selectedBed.Number }}号床 · {{ isOccupied(selectedBed) ? '详情' : '入住办理' }}</div>
              <div class="panel-sub">{{ isOccupied(selectedBed) ? '已入住，可办理退宿' : '选择班级与学生后确认入住' }}</div>
            </div>

            <!-- 已入住详情 -->
            <div v-if="isOccupied(selectedBed)" class="occupy-view">
              <div class="occupy-hero" :class="presenceToneClass(selectedBed.PresenceStatus)">
                <div class="occupy-hero-top">
                  <div class="avatar">
                    <img v-if="selectedBed.HeadPhotoImage" v-oss-src="selectedBed.HeadPhotoImage" alt="" />
                    <span v-else>{{ occupantInitial(selectedBed) }}</span>
                  </div>
                  <div class="hero-main">
                    <div class="name-row">
                      <span class="name">{{ selectedBed.OccupantName || '已入住' }}</span>
                      <span v-if="selectedBed.UserId > 0" class="tag-boarder">学生</span>
                      <span v-else class="tag-teacher">教师</span>
                    </div>
                    <div class="hero-sub" v-if="selectedBed.ClassName">
                      <span>{{ selectedBed.ClassName }}</span>
                    </div>
                  </div>
                  <div
                    v-if="selectedBed.UserId > 0"
                    class="status-pill"
                    :class="presenceClass(selectedBed.PresenceStatus)"
                    :title="presenceTitle(selectedBed)"
                  >
                    {{ selectedBed.PresenceStatus || '未打卡' }}
                  </div>
                  <div v-else class="status-pill is-in">已入住</div>
                </div>
                <div class="info-grid">
                  <div class="info-cell">
                    <div class="info-label">{{ selectedBed.UserId > 0 ? '学号' : '工号' }}</div>
                    <div class="info-value">{{ selectedBed.OccupantNo || '—' }}</div>
                  </div>
                  <div class="info-cell">
                    <div class="info-label">卡号</div>
                    <div class="info-value">{{ selectedBed.OccupantCardNo || '—' }}</div>
                  </div>
                  <div class="info-cell">
                    <div class="info-label">入住时间</div>
                    <div class="info-value">{{ selectedBed.CheckInTime || '—' }}</div>
                  </div>
                  <div class="info-cell">
                    <div class="info-label">最近打卡</div>
                    <div class="info-value">
                      <template v-if="selectedBed.UserId > 0">
                        {{ selectedBed.LastPunchTime || '暂无打卡' }}
                      </template>
                      <template v-else>教师床位不统计</template>
                    </div>
                  </div>
                </div>
              </div>

              <div class="punch-panel" v-if="selectedBed.UserId > 0">
                <div class="punch-head">
                  <span class="punch-title">当天流水</span>
                  <span class="punch-sub">{{ todayStr }} · 共 {{ dayPunchList.length }} 条</span>
                </div>
                <div v-loading="dayPunchLoading" class="punch-list">
                  <div
                    v-for="(item, idx) in dayPunchList"
                    :key="item.Id || idx"
                    class="punch-item"
                  >
                    <div class="punch-time">{{ punchTimeText(item) }}</div>
                    <div class="punch-main">
                      <span class="punch-dir" :class="punchDirClass(item)">{{ punchDirText(item) }}</span>
                      <span class="punch-meta" :title="punchMetaText(item)">{{ punchMetaText(item) }}</span>
                    </div>
                    <div class="punch-result" :title="punchResultText(item)">
                      {{ punchResultText(item) }}
                    </div>
                  </div>
                  <div v-if="!dayPunchLoading && !dayPunchList.length" class="punch-empty">
                    今日暂无门禁流水
                  </div>
                </div>
                <div class="status-note compact">
                  <i class="el-icon-info"></i>
                  <span>状态依据今日考勤流水与时段结果；刚入住且未刷卡显示「未打卡」</span>
                </div>
              </div>
              <div class="status-note" v-else>
                <i class="el-icon-info"></i>
                <span>教师床位仅办理入住/退宿，不参与在寝统计</span>
              </div>

              <div class="action-bar dual">
                <el-button
                  type="warning"
                  class="action-btn"
                  plain
                  :disabled="loading"
                  @click="openTransferDialog"
                >调换宿舍</el-button>
                <el-button type="danger" class="action-btn" :loading="loading" @click="checkout">退宿</el-button>
              </div>
            </div>

            <!-- 空闲入住 -->
            <div v-else class="checkin-view">
              <template v-if="room.RoomType === 1">
                <div class="checkin-body">
                  <div class="field">
                    <label>选择教师</label>
                    <el-select
                      v-model="teacherId"
                      filterable
                      clearable
                      placeholder="输入姓名检索教师"
                      style="width: 100%"
                      size="small"
                    >
                      <el-option
                        v-for="t in teacherList"
                        :key="t.Id"
                        :label="t.TeacherName"
                        :value="t.Id"
                      />
                    </el-select>
                  </div>
                </div>
                <div class="action-bar">
                  <el-button type="primary" class="action-btn" :loading="loading" @click="checkin">确认入住</el-button>
                </div>
              </template>

              <template v-else>
                <div class="checkin-body">
                  <div class="gender-banner">当前楼栋为{{ buildingGenderText }}宿舍；未设性别也可选，确认入住后将自动设为{{ buildingGenderText }}生</div>

                  <div class="field">
                    <label>班级 <span class="muted">校区 → 年级 → 班级</span></label>
                    <el-cascader
                      v-model="classPath"
                      :options="classTree"
                      :props="classProps"
                      clearable
                      filterable
                      change-on-select
                      :show-all-levels="false"
                      placeholder="请选择班级"
                      popper-class="dorm-class-cascader"
                      class="class-cascader"
                      size="small"
                      @change="onClassChange"
                    />
                    <div v-if="selectedClassPathText" class="class-path" :title="selectedClassPathText">
                      {{ selectedClassPathText }}
                    </div>
                  </div>

                    <div class="field stu-field">
                    <div class="stu-field-head">
                      <label><span class="req">*</span> 选择学生</label>
                      <span class="stu-count" v-if="currentClassId">
                        共 {{ studentOptions.length }} 人（已排除走读）
                        <span v-if="studentOptions.length >= 60"> · 可用上方搜索快速定位</span>
                      </span>
                    </div>
                    <el-input
                      v-model="studentKeyword"
                      clearable
                      size="small"
                      prefix-icon="el-icon-search"
                      placeholder="在已选范围内搜索姓名 / 学号 / 卡号"
                      :disabled="!currentClassId"
                      @input="onStudentKeywordInput"
                      @clear="onStudentKeywordInput('')"
                    />
                    <div v-loading="searching" class="stu-list">
                      <div
                        v-for="item in studentOptions"
                        :key="item.id"
                        class="stu-item"
                        :class="{ active: String(selectedStudentId) === String(item.id) }"
                        @click="pickStudent(item)"
                      >
                        <div class="stu-item-main">
                          <span class="stu-item-name">{{ item.name }}</span>
                          <span class="stu-item-sub">
                            <template v-if="item.no">{{ item.no }}</template>
                            <template v-if="item.className"> · {{ item.className }}</template>
                          </span>
                        </div>
                        <span class="stu-opt-tag" :class="liveTypeTagClass(item)">{{ liveTypeLabel(item) }}</span>
                      </div>
                      <div v-if="!currentClassId" class="stu-empty">请先选择班级，再挑选学生</div>
                      <div v-else-if="!searching && !studentOptions.length" class="stu-empty">
                        该范围内暂无符合条件的{{ buildingGenderText }}可入住学生（日间类型已过滤）
                      </div>
                    </div>
                  </div>
                </div>

                <div class="action-bar">
                  <div v-if="selectedStudent" class="selected-summary">
                    已选：<b>{{ selectedStudent.name }}</b>
                    <span v-if="selectedStudent.className"> · {{ selectedStudent.className }}</span>
                    <span class="stu-opt-tag" :class="liveTypeTagClass(selectedStudent)">{{ liveTypeLabel(selectedStudent) }}</span>
                  </div>
                  <el-button type="primary" class="action-btn" :loading="loading" @click="checkin">确认入住</el-button>
                </div>
              </template>
            </div>
          </template>
        </section>
      </div>

      <div v-else class="tip tip-none">非居住房：无床位办理</div>
    </div>

    <!-- 调宿弹窗 -->
    <el-dialog
      title="宿舍床位调换（调宿办理）"
      :visible.sync="transferVisible"
      width="480px"
      append-to-body
      :close-on-click-modal="false"
      custom-class="dorm-transfer-dialog"
      @close="resetTransferForm"
    >
      <el-form label-width="110px" size="small" class="transfer-form">
        <el-form-item label="当前调换人员">
          <div class="transfer-person">{{ transferPersonText }}</div>
        </el-form-item>
        <el-form-item label="目标宿舍楼" required>
          <el-select
            v-model="transferBuildingId"
            filterable
            placeholder="请选择宿舍楼"
            style="width: 100%"
            :loading="transferLoadingBuildings"
            @change="onTransferBuildingChange"
          >
            <el-option
              v-for="b in transferBuildings"
              :key="b.Id"
              :label="buildingOptionLabel(b)"
              :value="b.Id"
            />
          </el-select>
        </el-form-item>
        <el-form-item label="目标房间" required>
          <el-select
            v-model="transferRoomId"
            filterable
            placeholder="请选择目标房间"
            style="width: 100%"
            :disabled="!transferBuildingId"
            :loading="transferLoadingRooms"
            @change="onTransferRoomChange"
          >
            <el-option
              v-for="r in transferRooms"
              :key="r.Id"
              :label="roomOptionLabel(r)"
              :value="r.Id"
            />
          </el-select>
        </el-form-item>
        <el-form-item label="目标床位" required>
          <el-select
            v-model="transferBedId"
            filterable
            placeholder="请选择目标床位"
            style="width: 100%"
            :disabled="!transferRoomId"
          >
            <el-option
              v-for="bed in transferBeds"
              :key="bed.Id"
              :label="bedOptionLabel(bed)"
              :value="bed.Id"
              :disabled="String(bed.Id) === String(selectedBedId)"
            />
          </el-select>
        </el-form-item>
        <div class="transfer-hint">
          <i class="el-icon-warning-outline"></i>
          温馨提示：如果选择的床位有其他同学入住，则此操作会将两位同学的床位对调。
        </div>
      </el-form>
      <div slot="footer">
        <el-button size="small" @click="transferVisible = false">取消</el-button>
        <el-button type="primary" size="small" :loading="loading" :disabled="!transferBedId" @click="confirmTransfer">
          确认调换
        </el-button>
      </div>
    </el-dialog>
  </el-dialog>
</template>

<script>
import { bindBed, changeBed, getRoomsWithBeds, getBuildingsListPage } from '../../../api/modules/dormitory'
import { getTeacherMasterList, getStudentInfoListPage } from '../../../api/modules/personnel'
import { getHostelGatherRawHistoryListPage } from '../../../api/modules/attendance'
import { getTeachingDepartmentTree, getSubDepartmentTree } from '../../../api/modules/department'
import { fetchStudentLiveTypesByDeptId, liveTypeNameOf } from '../../../utils/studentLiveType'
import '../../../styles/dorm-module.css'

export default {
  name: 'DormBedManagerDialog',
  props: {
    visible: { type: Boolean, default: false },
    buildingId: { type: [Number, String], default: 0 },
    /** 楼栋性别：0 男生宿舍，1 女生宿舍 */
    buildingGender: { type: [Number, String], default: 0 },
    roomId: { type: [Number, String], default: 0 },
    schoolDepartmentId: { type: [Number, String], default: 0 },
    /** 看板已加载的房间快照，用于弹窗立刻渲染、避免再次等待接口 */
    roomSnapshot: { type: Object, default: null },
    /** 全校搜索关键字，用于床位高亮 */
    searchQuery: { type: String, default: '' }
  },
  data() {
    return {
      dialogVisible: false,
      room: null,
      selectedBedId: null,
      teacherId: null,
      teacherList: [],
      classTree: [],
      classPath: [],
      classProps: { value: 'value', label: 'label', children: 'children', checkStrictly: true, emitPath: true },
      flatStudents: [],
      searching: false,
      studentOptions: [],
      selectedStudentId: null,
      selectedStudent: null,
      studentKeyword: '',
      searchTimer: null,
      loading: false,
      roomRequestId: 0,
      /** 调宿弹窗 */
      transferVisible: false,
      transferBuildings: [],
      transferRooms: [],
      transferBeds: [],
      transferBuildingId: null,
      transferRoomId: null,
      transferBedId: null,
      transferLoadingBuildings: false,
      transferLoadingRooms: false,
      dayPunchList: [],
      dayPunchLoading: false,
      candidatesSchoolId: '',
      liveTypeList: []
    }
  },
  computed: {
    todayStr() {
      const d = new Date()
      const m = `${d.getMonth() + 1}`.padStart(2, '0')
      const day = `${d.getDate()}`.padStart(2, '0')
      return `${d.getFullYear()}-${m}-${day}`
    },
    dialogTitle() {
      if (!this.room) return '床位管理'
      const code = this.room.RoomCode || this.room.RoomName || ''
      const loc = [this.room.BuildingName, this.room.FloorName].filter(Boolean).join(' · ')
      return loc ? `【${loc} · ${code}室】床位管理与入住办理` : `【${code}】床位管理与入住办理`
    },
    /** 当前房间所属楼栋（全校搜索结果可能与左侧选中楼栋不一致） */
    effectiveBuildingId() {
      return this.buildingId
        || (this.room && this.room.BuildingId)
        || (this.roomSnapshot && this.roomSnapshot.BuildingId)
        || 0
    },
    selectedBed() {
      if (!this.room || !this.selectedBedId) return null
      return (this.room.Beds || []).find(b => String(b.Id) === String(this.selectedBedId)) || null
    },
    transferPersonText() {
      const bed = this.selectedBed
      if (!bed) return '-'
      const roomCode = (this.room && (this.room.RoomCode || this.room.RoomName)) || ''
      const name = bed.OccupantName || '入住人'
      return `${name}（${roomCode}室-${bed.Number}号床）`
    },
    bedCount() {
      return (this.room && this.room.Beds && this.room.Beds.length) || 0
    },
    occupiedCount() {
      if (!this.room || !this.room.Beds) return 0
      return this.room.Beds.filter(b => this.isOccupied(b)).length
    },
    bedGridClass() {
      return 'cols-2'
    },
    currentClassId() {
      if (!this.classPath || !this.classPath.length) return 0
      return this.classPath[this.classPath.length - 1]
    },
    /** 当前筛选节点下的班级 Id（选年级/校区时包含其下全部班级） */
    filterClassIds() {
      const id = this.currentClassId
      if (!id) return null
      const leaves = this.collectLeafIds(this.classTree, id)
      return leaves.length ? new Set(leaves.map(String)) : new Set([String(id)])
    },
    /**
     * 楼栋性别 → 学生 Sex（楼栋 0 男宿→Sex1；楼栋 1 女宿→Sex0）
     */
    requiredStudentSex() {
      const g = Number(this.buildingGender)
      if (g === 1) return 0
      return 1
    },
    buildingGenderText() {
      return Number(this.buildingGender) === 1 ? '女生' : '男生'
    },
    /** 完整班级路径，用于展示不被截断的信息 */
    selectedClassPathText() {
      if (!this.classPath || !this.classPath.length) return ''
      const labels = []
      let nodes = this.classTree
      for (const id of this.classPath) {
        const hit = (nodes || []).find(n => String(n.value) === String(id))
        if (!hit) break
        labels.push(hit.label)
        nodes = hit.children
      }
      return labels.join(' / ')
    }
  },
  watch: {
    visible: {
      immediate: true,
      handler(v) {
        this.dialogVisible = v
        if (v) this.open()
      }
    },
    roomId(val, oldVal) {
      if (this.visible && val && String(val) !== String(oldVal)) {
        this.open()
      }
    },
    selectedBedId() {
      this.loadDayPunches()
    }
  },
  beforeDestroy() {
    if (this.searchTimer) clearTimeout(this.searchTimer)
  },
  methods: {
    isOccupied(bed) {
      return !!(bed && (bed.UserId > 0 || bed.TeacherId > 0))
    },
    /**
     * 在寝状态短文案（床位卡）
     */
    presenceShort(status) {
      if (status === '已归寝') return '在寝'
      return status || '未打卡'
    },
    presenceTitle(bed) {
      const s = (bed && bed.PresenceStatus) || '未打卡'
      const t = bed && bed.LastPunchTime
      return t ? `${s} · 最近打卡 ${t}` : `${s}（今日尚无有效门禁流水）`
    },
    presenceClass(status) {
      const s = status || '未打卡'
      if (s === '已归寝' || s === '迟归') return 'is-in'
      if (s === '离寝') return 'is-out'
      if (s === '请假') return 'is-leave'
      if (s === '未归' || s === '未离') return 'is-miss'
      return 'is-unknown'
    },
    presenceTagType(status) {
      const s = status || '未打卡'
      if (s === '已归寝' || s === '迟归') return 'success'
      if (s === '离寝') return 'warning'
      if (s === '请假') return ''
      if (s === '未归' || s === '未离') return 'danger'
      return 'info'
    },
    presenceToneClass(status) {
      const s = status || '未打卡'
      if (s === '已归寝' || s === '迟归') return 'tone-in'
      if (s === '离寝') return 'tone-out'
      if (s === '请假') return 'tone-leave'
      if (s === '未归' || s === '未离') return 'tone-miss'
      return 'tone-unknown'
    },
    occupantInitial(bed) {
      const name = (bed && bed.OccupantName) || ''
      const t = String(name).trim()
      if (!t) return '?'
      if (/^[A-Za-z]/.test(t)) return t.charAt(0).toUpperCase()
      return t.slice(-1)
    },
    /**
     * 床位是否命中全校搜索关键字
     */
    isBedSearchMatched(bed) {
      const q = (this.searchQuery || '').trim().toLowerCase()
      if (!q || !bed || !this.isOccupied(bed)) return false
      const name = String(bed.OccupantName || '').toLowerCase()
      const no = String(bed.OccupantNo || '').toLowerCase()
      const card = String(bed.OccupantCardNo || '').toLowerCase()
      return name.indexOf(q) >= 0 || no.indexOf(q) >= 0 || card.indexOf(q) >= 0
    },
    async open() {
      const requestId = ++this.roomRequestId
      this.selectedBedId = null
      this.dayPunchList = []
      this.transferVisible = false
      this.resetTransferForm()
      this.resetCheckinForm()
      this.loadLiveTypeList()

      const snapshot = this.usableSnapshot()
      if (snapshot) {
        // 先用看板已有数据立刻展开，避免「正在加载房间数据」空窗。
        this.room = snapshot
        this.selectDefaultBed()
        this.loadCandidates()
        this.loadRoom(requestId)
        return
      }

      this.room = null
      await this.loadRoom(requestId)
      if (requestId !== this.roomRequestId) return
      this.selectDefaultBed()
      this.loadCandidates()
    },
    usableSnapshot() {
      const snap = this.roomSnapshot
      if (!snap || !this.roomId) return null
      return String(snap.Id) === String(this.roomId) ? snap : null
    },
    selectDefaultBed() {
      if (this.room && this.room.Beds && this.room.Beds.length) {
        const firstOcc = this.room.Beds.find(b => this.isOccupied(b))
        this.selectedBedId = (firstOcc || this.room.Beds[0]).Id
      }
    },
    resetCheckinForm() {
      this.teacherId = null
      this.classPath = []
      this.studentOptions = []
      this.selectedStudentId = null
      this.selectedStudent = null
      this.studentKeyword = ''
    },
    async loadRoom(requestId = this.roomRequestId) {
      const buildingId = this.effectiveBuildingId
      if (!buildingId) return
      try {
        const res = await getRoomsWithBeds({
          buildingId,
          roomId: this.roomId,
          departmentId: this.schoolDepartmentId || undefined
        })
        if (requestId === this.roomRequestId && res.data && res.data.success) {
          const list = res.data.response || []
          const fresh = list.find(r => String(r.Id) === String(this.roomId)) || null
          if (fresh) this.room = fresh
        }
      } catch (e) {
        // 后台刷新失败时保留已展示的快照
      }
    },
    async loadCandidates() {
      const deptId = this.schoolDepartmentId
      if (!deptId) return
      const deptIdStr = String(deptId)
      const needTeachers = this.room && this.room.RoomType === 1
      if (this.candidatesSchoolId === deptIdStr && this.classTree.length && (!needTeachers || this.teacherList.length)) {
        return
      }
      try {
        const [deptRes, subRes, teaRes] = await Promise.all([
          getTeachingDepartmentTree({ pid: deptIdStr, type: 1 }),
          getSubDepartmentTree({ pid: deptIdStr }),
          needTeachers
            ? getTeacherMasterList({ departmentId: deptIdStr })
            : Promise.resolve(null)
        ])
        // 优先组织架构级联（校区→年级→班级），不用全校班级平铺
        let cascader = []
        if (deptRes.data && deptRes.data.success && deptRes.data.response) {
          cascader = this.toClassCascader(deptRes.data.response)
        }
        if (!this.hasHierarchy(cascader) && subRes.data && subRes.data.success && subRes.data.response) {
          cascader = this.toClassCascader(subRes.data.response)
        }
        this.classTree = this.hasHierarchy(cascader) ? cascader : []
        if (teaRes && teaRes.data && teaRes.data.success) {
          const raw = teaRes.data.response
          this.teacherList = Array.isArray(raw) ? raw : (raw && raw.data) || []
        }
        this.candidatesSchoolId = deptIdStr
      } catch (e) {
        // ignore
      }
    },
    /** 是否为带下级的联动树（非平铺班级） */
    hasHierarchy(nodes) {
      return (nodes || []).some(n => n && n.children && n.children.length)
    },
    /**
     * 组织架构树 → 班级筛选级联（校区/年级/班级联动）
     */
    toClassCascader(root) {
      if (!root) return []
      const mapNode = (n) => {
        if (!n) return null
        const rawKids = n.children || n.Children || []
        const children = rawKids.map(mapNode).filter(Boolean)
        const value = n.value != null ? n.value : n.Value
        const label = n.label || n.Label
        if (label == null || value == null) return null
        if (children.length) {
          return { value, label, children }
        }
        return { value, label }
      }
      const unwrap = (node) => {
        if (!node) return []
        const kids = (node.children || node.Children || []).map(mapNode).filter(Boolean)
        const label = String(node.label || node.Label || '').trim()
        // 总平台 / 学校根：直接展开子级做联动入口
        if (kids.length && (label === '总平台' || label === '全部')) return kids
        if (kids.length) return kids
        const self = mapNode(node)
        return self ? [self] : []
      }
      if (Array.isArray(root)) {
        if (root.length === 1) return unwrap(root[0])
        return root.map(mapNode).filter(Boolean)
      }
      return unwrap(root)
    },
    /**
     * GetDepartmentStuTree 扁平班级（仅内部学生检索用，不作为筛选树）
     */
    toFlatClassTree(nodes) {
      return (nodes || [])
        .filter(n => n && n.value != null && String(n.value) !== '1001')
        .map(n => ({ value: n.value, label: n.label }))
    },
    collectLeafIds(nodes, targetId) {
      const find = (list) => {
        for (const n of list || []) {
          if (String(n.value) === String(targetId)) return this.leafIdsOf(n)
          const hit = find(n.children)
          if (hit) return hit
        }
        return null
      }
      return find(nodes) || []
    },
    leafIdsOf(node) {
      if (!node) return []
      if (!node.children || !node.children.length) return [String(node.value)]
      return node.children.reduce((acc, c) => acc.concat(this.leafIdsOf(c)), [])
    },
    flattenStudents(nodes, className = '') {
      const list = []
      ;(nodes || []).forEach(n => {
        const kids = n.children || []
        // 接口为扁平班级：子节点是学生（无 children）
        const hasDeptChild = kids.some(c => c.children && c.children.length)
        if (hasDeptChild) {
          list.push(...this.flattenStudents(kids, n.label))
        } else {
          kids.forEach(s => {
            list.push({
              id: s.value,
              name: s.label,
              no: s.stuCode || s.StuCode || s.cardNo || '',
              classId: n.value,
              className: n.label || className,
              sex: s.sex != null ? s.sex : (s.Sex != null ? s.Sex : null),
              liveTypeId: s.liveTypeId || s.LiveTypeId || '',
              liveType: s.liveType || s.LiveType || ''
            })
          })
        }
      })
      return list
    },
    /**
     * 是否走读（含特殊走读）
     */
    isDayLiveType(liveTypeId) {
      const t = (liveTypeId || '').toString()
      return t.indexOf('live_home') >= 0
    },
    /**
     * 是否明确住校
     */
    isBoardingLiveType(liveTypeId) {
      const t = (liveTypeId || '').toString()
      return !!t && t.indexOf('live_school') >= 0 && t.indexOf('live_home') < 0
    },
    /**
     * 住校类型展示文案。创建于 2026-08-15
     */
    async loadLiveTypeList() {
      this.liveTypeList = await fetchStudentLiveTypesByDeptId(this.schoolDepartmentId)
    },
    liveTypeLabel(item) {
      if (!item) return '未设置'
      if (item.liveType) return item.liveType
      const id = (item.liveTypeId || '').toString()
      const name = liveTypeNameOf(this.liveTypeList, id)
      if (name) return name
      if (id) return id
      return '未设置'
    },
    /**
     * 类型标签样式：住校 / 走读 / 未设置
     */
    liveTypeTagClass(item) {
      if (!item) return 'is-unset'
      if (this.isDayLiveType(item.liveTypeId)) return 'is-day'
      if (this.isBoardingLiveType(item.liveTypeId)) return 'is-board'
      if (item.liveTypeId) return 'is-set'
      return 'is-unset'
    },
    /**
     * 性别是否未设置（空或非 0/1）
     */
    isSexUnset(sex) {
      if (sex == null || sex === '') return true
      const n = Number(sex)
      return n !== 0 && n !== 1
    },
    matchBuildingGender(student) {
      if (!student) return false
      const need = this.requiredStudentSex
      // 未设性别：允许选入，入住时由后端按楼栋补全
      if (this.isSexUnset(student.sex)) return true
      return Number(student.sex) === Number(need)
    },
    localFiltered(kw) {
      const key = (kw || '').trim().toLowerCase()
      const allow = this.filterClassIds
      return this.flatStudents.filter(s => {
        if (this.isDayLiveType(s.liveTypeId)) return false
        if (!this.matchBuildingGender(s)) return false
        if (allow && !allow.has(String(s.classId))) return false
        if (!key) return true
        const blob = `${s.name || ''}${s.no || ''}${s.className || ''}`.toLowerCase()
        return blob.indexOf(key) >= 0
      }).slice(0, 30)
    },
    onClassChange() {
      this.clearStudent()
      this.studentKeyword = ''
      this.studentOptions = []
      if (!this.currentClassId) return
      // 选班后直接请求接口拉本班/本节点学生（不再只靠本地树缓存）
      this.loadClassStudents()
    },
    onStudentKeywordInput(val) {
      if (this.searchTimer) clearTimeout(this.searchTimer)
      const kw = typeof val === 'string' ? val : (this.studentKeyword || '')
      this.searchTimer = setTimeout(() => {
        if (!this.currentClassId) return
        this.loadClassStudents(kw)
      }, 280)
    },
    /**
     * 按当前班级（或年级/校区节点）加载学生列表
     */
    async loadClassStudents(kw = '') {
      if (!this.currentClassId) {
        this.studentOptions = []
        return
      }
      this.searching = true
      try {
        const key = (kw || '').trim()
        const para = {
          page: 1,
          intPageSize: 200,
          departmentId: String(this.currentClassId),
          sex: this.requiredStudentSex,
          forDormCheckIn: 1
        }
        if (key) {
          // 纯数字才按卡号；英文名按姓名搜（避免 tenghao 被当成卡号）
          if (/^\d+$/.test(key)) para.cardNo = key
          else para.stuName = key
        }
        const res = await getStudentInfoListPage(para)
        let remote = []
        if (res.data && res.data.success) {
          const rows = (res.data.response && res.data.response.data) || []
          remote = rows.map(s => ({
            id: s.Id,
            name: s.StuName,
            no: s.StuCode || s.CardNumber || '',
            classId: s.DepartmentId,
            className: s.DepartmentName || '',
            sex: s.Sex,
            liveTypeId: s.LiveTypeId || '',
            liveType: s.LiveType || ''
          }))
          // 接口已按宿舍规则过滤；前端再兜底排除走读与对立性别
          remote = remote.filter(s => {
            if (this.isDayLiveType(s.liveTypeId)) return false
            if (this.isSexUnset(s.sex)) return true
            return Number(s.sex) === Number(this.requiredStudentSex)
          })
        }
        // 本地树补充（有关键字时也合并）
        const local = this.localFiltered(key)
        const map = new Map()
        ;[...remote, ...local].forEach(s => {
          if (s && s.id != null) map.set(String(s.id), s)
        })
        this.studentOptions = Array.from(map.values()).slice(0, 200)
      } catch (e) {
        this.studentOptions = this.localFiltered(kw)
      } finally {
        this.searching = false
      }
    },
    pickStudent(item) {
      if (!item) return
      if (this.isDayLiveType(item.liveTypeId)) {
        this.$message.warning('该人员类型不能办理入住')
        return
      }
      if (!this.matchBuildingGender(item) && !this.isSexUnset(item.sex)) {
        this.$message.warning(`该楼栋为${this.buildingGenderText}宿舍，请选择${this.buildingGenderText}生`)
        return
      }
      this.selectedStudentId = item.id
      this.selectedStudent = item
    },
    onStudentSelectFocus() {
      if (!this.studentOptions.length && this.currentClassId) {
        this.loadClassStudents(this.studentKeyword || '')
      }
    },
    remoteSearchStudents(query) {
      // 兼容旧逻辑：关键字走本班加载
      this.studentKeyword = query || ''
      this.onStudentKeywordInput(query)
    },
    async searchRemote(kw) {
      await this.loadClassStudents(kw)
    },
    onStudentChange(id) {
      if (id == null || id === '') {
        this.clearStudent()
        return
      }
      const hit = this.studentOptions.find(s => String(s.id) === String(id))
      if (hit && this.isDayLiveType(hit.liveTypeId)) {
        this.$message.warning('该人员类型不能办理入住')
        this.clearStudent()
        return
      }
      if (hit && !this.matchBuildingGender(hit) && !this.isSexUnset(hit.sex)) {
        this.$message.warning(`该楼栋为${this.buildingGenderText}宿舍，请选择${this.buildingGenderText}生`)
        this.clearStudent()
        return
      }
      this.selectedStudent = hit || { id, name: String(id) }
      this.selectedStudentId = id
    },
    clearStudent() {
      this.selectedStudentId = null
      this.selectedStudent = null
    },
    selectBed(bed) {
      this.selectedBedId = bed.Id
      this.resetCheckinForm()
    },
    /**
     * 加载当前床位学生今日门禁流水
     */
    async loadDayPunches() {
      const bed = this.selectedBed
      if (!bed || !(bed.UserId > 0)) {
        this.dayPunchList = []
        return
      }
      this.dayPunchLoading = true
      try {
        const res = await getHostelGatherRawHistoryListPage({
          page: 1,
          intPageSize: 50,
          date: this.todayStr,
          userAccountUid: String(bed.UserId),
          departmentId: this.schoolDepartmentId || undefined
        })
        const list = (res.data && res.data.response && res.data.response.data) || []
        this.dayPunchList = list
      } catch (e) {
        this.dayPunchList = []
      } finally {
        this.dayPunchLoading = false
      }
    },
    punchTimeText(item) {
      if (!item) return '—'
      if (item.Time) return item.Time
      const t = item.TouchTime || ''
      if (t.length >= 19) return t.slice(11, 19)
      if (t.length >= 16) return t.slice(11, 16)
      return t || '—'
    },
    punchDirText(item) {
      if (!item) return '—'
      if (item.ResolvedDirectionName) return item.ResolvedDirectionName
      const t = item.TouchType || ''
      if (t === 'in' || t === '进入' || t.indexOf('进入') >= 0 || t.indexOf('归') >= 0) return '进入'
      if (t === 'out' || t === '离开' || t.indexOf('离开') >= 0 || t.indexOf('离') >= 0) return '离开'
      if (t === 'inout' || t === '出入') return '出入'
      return t || '—'
    },
    punchDirClass(item) {
      const t = this.punchDirText(item)
      if (t === '进入' || t === '归寝') return 'is-in'
      if (t === '离开' || t === '离寝') return 'is-out'
      return ''
    },
    punchMetaText(item) {
      if (!item) return ''
      return item.KqTerminalName || item.KqTerminalId || item.TouchPosition || ''
    },
    /**
     * 门禁流水的处理结果是后端内部事件码，页面统一显示中文。
     */
    punchResultText(item) {
      if (!item) return '—'
      const value = item.MatchStatusName || item.ProcessResult || item.RejectReason || item.MatchStatus || ''
      if (!value) return '—'
      const map = {
        DormPresenceUpdated: '已更新在寝状态',
        DormAttendanceDisabled: '未启用宿舍考勤',
        TerminalDirectionInvalid: '终端方向无效',
        DirectionConflict: '方向冲突',
        StudentNotFound: '未找到学生',
        NotResident: '无入住资格',
        NotCheckedIn: '未入住',
        BuildingMismatch: '楼栋不匹配',
        Matched: '已匹配',
        Ambiguous: '待确认',
        Rejected: '已拒绝',
        Ignored: '已忽略'
      }
      return map[value] || value
    },
    buildingOptionLabel(b) {
      if (!b) return ''
      const gender = Number(b.Gender) === 1 ? '女生' : '男生'
      return `${b.Name || b.Code || b.Id}（${gender}）`
    },
    roomOptionLabel(r) {
      if (!r) return ''
      const code = r.RoomCode || r.RoomName || r.Id
      const type = r.RoomType === 1 ? '宿管' : (r.RoomType === 2 ? '非居住' : '学生')
      return `${code} · ${type} · ${r.Occupied || 0}/${r.Capacity || 0}`
    },
    bedOptionLabel(bed) {
      if (!bed) return ''
      if (this.isOccupied(bed)) {
        return `${bed.Number}号床 · ${bed.OccupantName || '已入住'}（对调）`
      }
      return `${bed.Number}号床 · 空闲`
    },
    async openTransferDialog() {
      if (!this.selectedBed || !this.isOccupied(this.selectedBed)) return
      this.resetTransferForm()
      this.transferVisible = true
      await this.loadTransferBuildings()
      // 默认当前楼栋
      if (this.effectiveBuildingId) {
        this.transferBuildingId = this.effectiveBuildingId
        await this.onTransferBuildingChange(this.effectiveBuildingId)
        if (this.roomId) {
          this.transferRoomId = this.roomId
          this.onTransferRoomChange(this.roomId)
        }
      }
    },
    resetTransferForm() {
      this.transferBuildingId = null
      this.transferRoomId = null
      this.transferBedId = null
      this.transferRooms = []
      this.transferBeds = []
    },
    async loadTransferBuildings() {
      this.transferLoadingBuildings = true
      try {
        const res = await getBuildingsListPage({
          page: 1,
          intPageSize: 200,
          departmentId: this.schoolDepartmentId || ''
        })
        if (res.data && res.data.success) {
          this.transferBuildings = (res.data.response && res.data.response.data) || []
        } else {
          this.transferBuildings = []
        }
      } finally {
        this.transferLoadingBuildings = false
      }
    },
    async onTransferBuildingChange(id) {
      this.transferRoomId = null
      this.transferBedId = null
      this.transferBeds = []
      this.transferRooms = []
      if (!id) return
      this.transferLoadingRooms = true
      try {
        const res = await getRoomsWithBeds({ buildingId: id })
        if (res.data && res.data.success) {
          const rows = res.data.response || []
          const isStu = this.selectedBed && this.selectedBed.UserId > 0
          this.transferRooms = rows.filter(r => {
            if (r.RoomType === 2) return false
            if (isStu) return r.RoomType !== 1
            return r.RoomType === 1
          })
        }
      } finally {
        this.transferLoadingRooms = false
      }
    },
    onTransferRoomChange(id) {
      this.transferBedId = null
      const room = (this.transferRooms || []).find(r => String(r.Id) === String(id))
      this.transferBeds = (room && room.Beds) ? room.Beds.slice() : []
    },
    async confirmTransfer() {
      if (!this.selectedBed || !this.transferBedId) {
        return this.$message.warning('请选择目标床位')
      }
      if (String(this.selectedBed.Id) === String(this.transferBedId)) {
        return this.$message.warning('目标床位不能与当前床位相同')
      }
      const targetBed = (this.transferBeds || []).find(b => String(b.Id) === String(this.transferBedId))
      const targetOcc = targetBed && this.isOccupied(targetBed)
      const tip = targetOcc
        ? `确认将「${this.selectedBed.OccupantName || '入住人'}」与「${targetBed.OccupantName || '对方'}」的床位对调？`
        : `确认将「${this.selectedBed.OccupantName || '入住人'}」调至所选床位？`
      try {
        await this.$confirm(tip, '调换宿舍', { type: 'warning' })
      } catch (e) {
        return
      }
      this.loading = true
      try {
        const res = await changeBed({
          SourceBedId: this.selectedBed.Id,
          TargetBedId: this.transferBedId
        })
        if (res.data && res.data.success) {
          const targetId = this.transferBedId
          this.$message.success(res.data.msg || '调宿成功')
          this.transferVisible = false
          await this.loadRoom()
          this.selectedBedId = targetId
          this.$emit('changed')
        } else {
          this.$message.error((res.data && res.data.msg) || '调宿失败')
        }
      } finally {
        this.loading = false
      }
    },
    async checkin() {
      if (!this.selectedBed) return
      const payload = { BedId: this.selectedBed.Id, Type: 0 }
      if (this.room.RoomType === 1) {
        if (!this.teacherId) return this.$message.warning('请选择教师')
        payload.TeacherId = this.teacherId
      } else {
        if (!this.selectedStudentId) return this.$message.warning('请选择学生')
        if (this.selectedStudent && this.isDayLiveType(this.selectedStudent.liveTypeId)) {
          return this.$message.warning('该人员类型不能办理入住')
        }
        if (this.selectedStudent && !this.isSexUnset(this.selectedStudent.sex)
          && !this.matchBuildingGender(this.selectedStudent)) {
          return this.$message.warning(`该楼栋为${this.buildingGenderText}宿舍，只能安排${this.buildingGenderText}生`)
        }
        payload.StudentId = this.selectedStudentId
      }
      this.loading = true
      try {
        const res = await bindBed(payload)
        if (res.data && res.data.success) {
          this.$message.success(res.data.msg || '入住成功')
          await this.loadRoom()
          this.$emit('changed')
          this.resetCheckinForm()
        } else {
          this.$message.error((res.data && res.data.msg) || '入住失败')
        }
      } finally {
        this.loading = false
      }
    },
    async checkout() {
      if (!this.selectedBed) return
      await this.$confirm('确认退宿？', '提示', { type: 'warning' })
      this.loading = true
      try {
        const res = await bindBed({ BedId: this.selectedBed.Id, Type: 1 })
        if (res.data && res.data.success) {
          this.$message.success(res.data.msg || '退宿成功')
          await this.loadRoom()
          this.$emit('changed')
          this.resetCheckinForm()
        } else {
          this.$message.error((res.data && res.data.msg) || '退宿失败')
        }
      } finally {
        this.loading = false
      }
    },
    handleClose() {
      this.$emit('update:visible', false)
      this.$emit('close')
    }
  }
}
</script>

<style scoped>
.bed-mgr {
  max-height: calc(86vh - 88px);
}
.bed-loading {
  min-height: 260px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  color: #64748b;
}
.bed-layout {
  display: flex;
  gap: 14px;
  height: min(620px, calc(86vh - 100px));
}
.bed-left,
.bed-right {
  background: #fff;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  padding: 14px;
  display: flex;
  flex-direction: column;
  min-height: 0;
  overflow: hidden;
}
.bed-left {
  flex: 1 1 46%;
  min-width: 0;
}
.bed-right {
  flex: 1 1 54%;
  max-width: none;
  min-width: 320px;
}
.panel-head {
  margin-bottom: 8px;
  flex-shrink: 0;
}
.panel-title {
  font-size: 15px;
  font-weight: 700;
  color: #134e4a;
}
.panel-sub {
  margin-top: 4px;
  font-size: 12px;
  color: #64748b;
}
.beds-grid {
  display: grid;
  gap: 8px;
  overflow-y: auto;
  flex: 1;
  padding: 2px 4px 8px 0;
  align-content: start;
  align-items: stretch;
  grid-auto-rows: 104px;
}
.beds-grid.cols-2 {
  grid-template-columns: repeat(2, minmax(0, 1fr));
}
.beds-grid.cols-3 {
  grid-template-columns: repeat(3, minmax(0, 1fr));
}
.bed-card {
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  padding: 10px 12px;
  cursor: pointer;
  background: #f8fafc;
  transition: border-color 0.15s ease, background 0.15s ease, box-shadow 0.15s ease;
  box-sizing: border-box;
  height: 104px;
  min-width: 0;
  display: flex;
  flex-direction: column;
}
.bed-card:hover {
  border-color: #99f6e4;
  background: #f0fdfa;
}
.bed-card.occupied {
  background: #fff;
}
.bed-card.active {
  border-color: #0d9488;
  background: #ecfeff;
  box-shadow: 0 0 0 2px rgba(13, 148, 136, 0.14);
}
.bed-card.matched {
  border-color: #f59e0b;
  box-shadow: 0 0 0 2px rgba(245, 158, 11, 0.28);
}
.bed-card-head {
  min-height: 20px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
}
.bed-no {
  font-weight: 700;
  font-size: 13px;
  color: #0f172a;
  white-space: nowrap;
}
.bed-person {
  flex: 1;
  min-width: 0;
  display: flex;
  align-items: center;
  gap: 8px;
  margin-top: 8px;
}
.bed-avatar {
  width: 32px;
  height: 32px;
  border-radius: 6px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex: 0 0 32px;
  color: #0f766e;
  background: #ccfbf1;
  font-size: 14px;
  font-weight: 700;
  overflow: hidden;
}
.bed-avatar img,
.avatar img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
}
.bed-person-main {
  flex: 1;
  min-width: 0;
}
.bed-person .name {
  font-size: 13px;
  font-weight: 600;
  color: #134e4a;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.bed-person .sub {
  font-size: 12px;
  color: #64748b;
  margin-top: 2px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.tag-boarder,
.tag-teacher {
  display: inline-flex;
  align-items: center;
  height: 18px;
  padding: 0 6px;
  border-radius: 4px;
  font-size: 11px;
  font-weight: 600;
  flex-shrink: 0;
}
.tag-boarder {
  background: #dbeafe;
  color: #1d4ed8;
}
.tag-teacher {
  background: #ffedd5;
  color: #c2410c;
}
.tag-presence {
  font-size: 11px;
  font-weight: 600;
  padding: 0 6px;
  height: 18px;
  line-height: 18px;
  display: inline-flex;
  align-items: center;
  border-radius: 4px;
  flex-shrink: 0;
  white-space: nowrap;
}
.tag-presence.is-in {
  background: #d1fae5;
  color: #047857;
}
.tag-presence.is-out {
  background: #ffedd5;
  color: #c2410c;
}
.tag-presence.is-leave {
  background: #e0e7ff;
  color: #4338ca;
}
.tag-presence.is-miss {
  background: #fee2e2;
  color: #b91c1c;
}
.tag-presence.is-unknown {
  background: #f1f5f9;
  color: #64748b;
  border: none;
}
.name-row {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
  margin-bottom: 0;
}
.occupy-view {
  gap: 12px;
}
.occupy-hero {
  flex-shrink: 0;
  border-radius: 14px;
  padding: 16px;
  border: 1px solid #ccfbf1;
  background:
    linear-gradient(165deg, rgba(204, 251, 241, 0.85) 0%, rgba(240, 253, 250, 0.95) 42%, #fff 100%);
  box-shadow: 0 1px 0 rgba(15, 23, 42, 0.02);
}
.occupy-hero.tone-out {
  border-color: #fed7aa;
  background:
    linear-gradient(165deg, rgba(255, 237, 213, 0.9) 0%, rgba(255, 247, 237, 0.95) 42%, #fff 100%);
}
.occupy-hero.tone-leave {
  border-color: #c7d2fe;
  background:
    linear-gradient(165deg, rgba(224, 231, 255, 0.9) 0%, rgba(238, 242, 255, 0.95) 42%, #fff 100%);
}
.occupy-hero.tone-miss {
  border-color: #fecaca;
  background:
    linear-gradient(165deg, rgba(254, 226, 226, 0.9) 0%, rgba(254, 242, 242, 0.95) 42%, #fff 100%);
}
.occupy-hero.tone-unknown {
  border-color: #e2e8f0;
  background: linear-gradient(165deg, #f8fafc 0%, #fff 100%);
}
.occupy-hero-top {
  display: flex;
  align-items: flex-start;
  gap: 12px;
}
.hero-main {
  flex: 1;
  min-width: 0;
}
.hero-sub {
  margin-top: 4px;
  font-size: 12px;
  color: #64748b;
  line-height: 1.4;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.hero-sub .dot {
  margin: 0 4px;
  color: #cbd5e1;
}
.status-pill {
  flex-shrink: 0;
  height: 26px;
  padding: 0 10px;
  border-radius: 999px;
  font-size: 12px;
  font-weight: 700;
  display: inline-flex;
  align-items: center;
}
.status-pill.is-in {
  background: #059669;
  color: #fff;
}
.status-pill.is-out {
  background: #ea580c;
  color: #fff;
}
.status-pill.is-leave {
  background: #4f46e5;
  color: #fff;
}
.status-pill.is-miss {
  background: #dc2626;
  color: #fff;
}
.status-pill.is-unknown {
  background: #64748b;
  color: #fff;
}
.info-grid {
  margin-top: 14px;
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 8px;
}
.info-cell {
  padding: 10px 12px;
  border-radius: 10px;
  background: rgba(255, 255, 255, 0.78);
  border: 1px solid rgba(226, 232, 240, 0.9);
}
.info-label {
  font-size: 11px;
  color: #94a3b8;
  font-weight: 600;
  margin-bottom: 4px;
}
.info-value {
  font-size: 13px;
  font-weight: 650;
  color: #0f172a;
  line-height: 1.35;
  word-break: break-all;
}
.status-note {
  display: flex;
  align-items: flex-start;
  gap: 8px;
  padding: 10px 12px;
  border-radius: 10px;
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  color: #64748b;
  font-size: 12px;
  line-height: 1.45;
}
.status-note.compact {
  margin-top: 8px;
  padding: 8px 10px;
  flex-shrink: 0;
}
.status-note i {
  margin-top: 2px;
  color: #14b8a6;
  flex-shrink: 0;
}
.punch-panel {
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
}
.punch-head {
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  margin-bottom: 8px;
  flex-shrink: 0;
}
.punch-title {
  font-size: 13px;
  font-weight: 700;
  color: #134e4a;
}
.punch-sub {
  font-size: 12px;
  color: #94a3b8;
}
.punch-list {
  flex: 1;
  min-height: 140px;
  max-height: 260px;
  overflow-x: hidden;
  overflow-y: auto;
  overscroll-behavior: contain;
  -webkit-overflow-scrolling: touch;
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  background: #fff;
}
.punch-item {
  display: grid;
  /* 结果列按内容宽度，把剩余空间留给位置文案，避免被挤成省略号 */
  grid-template-columns: 64px minmax(0, 1fr) auto;
  gap: 8px;
  align-items: start;
  padding: 8px 10px;
  border-bottom: 1px solid #f1f5f9;
}
.punch-item:last-child {
  border-bottom: none;
}
.punch-time {
  font-size: 13px;
  font-weight: 700;
  color: #0f172a;
  font-variant-numeric: tabular-nums;
  line-height: 20px;
}
.punch-main {
  min-width: 0;
  display: flex;
  align-items: flex-start;
  gap: 8px;
}
.punch-dir {
  flex-shrink: 0;
  height: 20px;
  padding: 0 6px;
  border-radius: 4px;
  font-size: 11px;
  font-weight: 700;
  display: inline-flex;
  align-items: center;
  background: #f1f5f9;
  color: #475569;
}
.punch-dir.is-in {
  background: #d1fae5;
  color: #047857;
}
.punch-dir.is-out {
  background: #ffedd5;
  color: #c2410c;
}
.punch-meta {
  min-width: 0;
  flex: 1;
  font-size: 12px;
  color: #64748b;
  line-height: 20px;
  word-break: break-all;
}
.punch-result {
  font-size: 12px;
  color: #64748b;
  text-align: right;
  white-space: nowrap;
  line-height: 20px;
}
.punch-empty {
  padding: 28px 12px;
  text-align: center;
  color: #94a3b8;
  font-size: 13px;
}
.occupy-view .action-bar {
  margin-top: auto;
  padding-top: 4px;
  flex-shrink: 0;
}
.avatar {
  width: 48px;
  height: 48px;
  border-radius: 14px;
  background: linear-gradient(145deg, #14b8a6, #0f766e);
  color: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 18px;
  font-weight: 700;
  flex-shrink: 0;
  box-shadow: 0 6px 14px rgba(13, 148, 136, 0.28);
  overflow: hidden;
}
.name-row .name {
  font-size: 17px;
  font-weight: 750;
  color: #134e4a;
}
.action-bar.dual {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 8px;
}
.bed-empty {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 4px;
  color: #94a3b8;
  font-size: 12px;
  padding: 4px 0;
}
.bed-empty i {
  font-size: 14px;
}
.empty-pane {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  color: #94a3b8;
  gap: 6px;
}
.empty-pane i {
  font-size: 36px;
  color: #5eead4;
  margin-bottom: 4px;
}
.empty-pane p {
  margin: 0;
  color: #334155;
  font-weight: 600;
}
.occupy-view,
.checkin-view {
  display: flex;
  flex-direction: column;
  flex: 1;
  min-height: 0;
}
.checkin-body {
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}
.gender-banner {
  flex-shrink: 0;
  margin-bottom: 8px;
  padding: 6px 10px;
  border-radius: 8px;
  background: #f0fdfa;
  border: 1px solid #99f6e4;
  color: #0f766e;
  font-size: 12px;
  line-height: 1.35;
}
.class-cascader {
  width: 100%;
}
.class-path {
  margin-top: 4px;
  padding: 4px 8px;
  border-radius: 6px;
  background: #f8fafc;
  color: #475569;
  font-size: 12px;
  line-height: 1.4;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.stu-field {
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
  margin-bottom: 0;
}
.stu-field-head {
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  margin-bottom: 6px;
}
.stu-field-head label {
  margin-bottom: 0;
}
.stu-count {
  font-size: 12px;
  color: #94a3b8;
}
.stu-list {
  flex: 1;
  min-height: 200px;
  max-height: none;
  margin-top: 6px;
  overflow: auto;
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  background: #fff;
}
.stu-item {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  padding: 7px 10px;
  border-bottom: 1px solid #f1f5f9;
  cursor: pointer;
  transition: background .12s ease;
}
.stu-item:last-child {
  border-bottom: none;
}
.stu-item:hover {
  background: #f0fdfa;
}
.stu-item.active {
  background: #ccfbf1;
}
.stu-item-main {
  min-width: 0;
  flex: 1;
  display: flex;
  align-items: baseline;
  gap: 8px;
  overflow: hidden;
}
.stu-item-name {
  flex-shrink: 0;
  font-size: 13px;
  font-weight: 650;
  color: #0f172a;
}
.stu-item-sub {
  min-width: 0;
  font-size: 12px;
  color: #64748b;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.stu-empty {
  padding: 28px 12px;
  text-align: center;
  color: #94a3b8;
  font-size: 13px;
}
.selected-summary {
  margin-bottom: 8px;
  font-size: 13px;
  color: #334155;
}
.selected-summary b {
  color: #0f766e;
}
.field {
  margin-bottom: 8px;
  position: relative;
}
.field label {
  display: block;
  font-size: 13px;
  color: #334155;
  margin-bottom: 6px;
  font-weight: 600;
}
.field .req {
  color: #e11d48;
}
.field .muted {
  font-weight: 400;
  color: #94a3b8;
  font-size: 12px;
}
.field-tip {
  margin: -2px 0 8px;
  font-size: 12px;
  color: #0f766e;
  line-height: 1.4;
}
.action-bar {
  margin-top: auto;
  padding-top: 16px;
}
.action-btn {
  width: 100%;
}
.tip-none {
  padding: 40px;
  text-align: center;
  color: #64748b;
  background: #f8fafc;
  border-radius: 10px;
}
</style>

<style>
.dorm-bed-dialog {
  border-radius: 12px;
  overflow: hidden;
}
.dorm-bed-dialog .el-dialog__header {
  padding: 14px 18px 8px;
}
.dorm-bed-dialog .el-dialog__body {
  padding: 4px 16px 16px;
}
.dorm-bed-dialog .el-dialog__title {
  font-weight: 700;
  color: #134e4a;
  font-size: 16px;
}
.dorm-bed-dialog .el-button--primary {
  background-color: #0d9488;
  border-color: #0d9488;
}
.dorm-bed-dialog .el-button--primary:hover,
.dorm-bed-dialog .el-button--primary:focus {
  background-color: #0f766e;
  border-color: #0f766e;
}
/* 学生下拉：与旧版一致的姓名+班级+住校标签 */
.dorm-stu-select-dropdown .el-select-dropdown__item {
  height: auto;
  line-height: 1.4;
  padding: 8px 12px;
}
.dorm-stu-select-dropdown .stu-opt {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
}
.dorm-stu-select-dropdown .stu-opt-main {
  min-width: 0;
  flex: 1;
}
.dorm-stu-select-dropdown .stu-opt-name {
  font-size: 14px;
  font-weight: 600;
  color: #0f172a;
}
.dorm-stu-select-dropdown .stu-opt-sub {
  margin-top: 2px;
  font-size: 12px;
  color: #64748b;
}
.dorm-stu-select-dropdown .stu-opt-tag {
  flex-shrink: 0;
  font-size: 12px;
  color: #0f766e;
  background: #ecfeff;
  border: 1px solid #99f6e4;
  border-radius: 4px;
  padding: 2px 6px;
  line-height: 1.4;
}
.stu-opt-tag {
  flex-shrink: 0;
  font-size: 11px;
  color: #0f766e;
  background: #ecfeff;
  border: 1px solid #99f6e4;
  border-radius: 4px;
  padding: 1px 5px;
  line-height: 1.3;
}
.stu-opt-tag.is-board {
  color: #0f766e;
  background: #ecfeff;
  border-color: #99f6e4;
}
.stu-opt-tag.is-day {
  color: #b45309;
  background: #fffbeb;
  border-color: #fde68a;
}
.stu-opt-tag.is-set {
  color: #2563eb;
  background: #eff6ff;
  border-color: #bfdbfe;
}
.stu-opt-tag.is-unset {
  color: #64748b;
  background: #f8fafc;
  border-color: #e2e8f0;
}
.selected-summary .stu-opt-tag {
  margin-left: 8px;
  vertical-align: middle;
}
.dorm-class-cascader {
  min-width: 280px;
}
.dorm-class-cascader .el-cascader-menu {
  min-width: 160px;
}
.dorm-transfer-dialog .transfer-person {
  color: #0f766e;
  font-weight: 600;
  line-height: 32px;
}
.dorm-transfer-dialog .transfer-hint {
  margin: 4px 0 0 110px;
  padding: 8px 10px;
  border-radius: 8px;
  background: #fffbeb;
  border: 1px solid #fde68a;
  color: #b45309;
  font-size: 12px;
  line-height: 1.5;
}
.dorm-transfer-dialog .transfer-hint i {
  margin-right: 4px;
}
</style>
